import type { AssessmentResults, ContactInfo } from '../types';

// This will be configured with your Google Apps Script Web App URL
// @ts-ignore - Vite env variable
const GOOGLE_SHEETS_URL = import.meta.env?.VITE_GOOGLE_SHEETS_URL || '';

interface SubmissionData extends ContactInfo {
  overallScore: number;
  businessStage: string;
  primaryQuadrant: string;
  primaryChallenge: string;
  aiExperience: string;
  aiBudget: string;
  ethicsImportance: string;
  vitalSigns: {
    team: number;
    audience: number;
    leads: number;
    meetings: number;
    content: number;
    sales: number;
    partnerships: number;
    profit: number;
  };
  quadrantScores: {
    revenue: number;
    brand: number;
    audience: number;
    operations: number;
  };
}

export async function submitToGoogleSheets(
  contactInfo: ContactInfo, 
  assessmentResults: AssessmentResults
): Promise<boolean> {
  
  // For testing without Google Sheets URL
  if (!GOOGLE_SHEETS_URL) {
    console.log('Google Sheets submission (simulated):', {
      contactInfo,
      assessmentResults
    });
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    return true;
  }

  try {
    const submissionData: SubmissionData = {
      ...contactInfo,
      overallScore: assessmentResults.overallScore,
      businessStage: assessmentResults.businessStage,
      primaryQuadrant: assessmentResults.primaryQuadrant,
      primaryChallenge: assessmentResults.primaryChallenge,
      aiExperience: assessmentResults.aiExperience,
      aiBudget: assessmentResults.aiBudget,
      ethicsImportance: assessmentResults.ethicsImportance,
      vitalSigns: assessmentResults.vitalSigns,
      quadrantScores: assessmentResults.quadrantScores
    };

    await fetch(GOOGLE_SHEETS_URL, {
      method: 'POST',
      mode: 'no-cors', // Required for Google Apps Script
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submissionData)
    });

    // Note: With no-cors mode, we can't read the response
    // But if no error is thrown, we assume success
    return true;

  } catch (error) {
    console.error('Error submitting to Google Sheets:', error);
    throw new Error('Failed to submit data. Please try again.');
  }
}

// Utility function to validate the Google Sheets connection
export async function validateGoogleSheetsConnection(): Promise<boolean> {
  if (!GOOGLE_SHEETS_URL) {
    return false;
  }
  
  try {
    await fetch(GOOGLE_SHEETS_URL, { 
      method: 'OPTIONS',
      mode: 'no-cors'
    });
    return true;
  } catch {
    return false;
  }
}